import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { AuthService } from '../../../../shared/services/auth.service';
import { TenantService } from '../../../../shared/services/tenant.service';
import { VOICE_CALL_LABELS } from '../constants/voice-call.constants';
import { CallUiState, VoiceCallerContext } from '../models/voice-call.models';
import { ClientStreamService } from './client-stream.service';
import { TwilioCallService } from './twilio-call.service';
import { VoiceTokenService } from './voice-token.service';

@Injectable({ providedIn: 'root' })
export class CallSessionService implements OnDestroy {
  private readonly tokenService = inject(VoiceTokenService);
  private readonly stream = inject(ClientStreamService);
  private readonly twilio = inject(TwilioCallService);
  private readonly auth = inject(AuthService);
  private readonly tenant = inject(TenantService);

  readonly isStarting = signal(false);
  readonly pageError = signal<string | null>(null);
  readonly currentSessionId = signal<string | null>(null);
  readonly drawerOpen = signal(false);

  readonly uiState = computed<CallUiState>(() =>
    this.mergeState(this.twilio.twilioState(), this.stream.streamState()),
  );

  readonly transcriptLines = this.stream.transcriptLines;
  readonly isMuted = this.twilio.isMuted;
  readonly isSpeakerOn = this.twilio.isSpeakerOn;
  readonly wsConnected = this.stream.wsConnected;

  readonly canStart = computed(
    () => !this.isStarting() && this.uiState() !== 'active' && this.uiState() !== 'connecting',
  );

  readonly inCall = computed(() => this.uiState() === 'active' || this.uiState() === 'connecting');

  openDrawer(): void {
    this.drawerOpen.set(true);
    this.stream.connect();
  }

  closeDrawer(): void {
    if (this.inCall()) {
      this.endCall();
    }
    this.drawerOpen.set(false);
  }

  ngOnDestroy(): void {
    this.stream.disconnect();
    this.twilio.endCall();
  }

  async startCall(): Promise<void> {
    if (!this.canStart()) {
      return;
    }

    this.isStarting.set(true);
    this.pageError.set(null);
    this.stream.clearTranscript();

    try {
      const caller = this.buildCallerContext();
      if (!caller) {
        throw new Error('Not signed in');
      }
      const sessionId = crypto.randomUUID();
      this.currentSessionId.set(sessionId);
      const token = await this.tokenService.fetchToken(sessionId, caller.userId);
      await this.twilio.startCall(token, sessionId, caller);
    } catch (err) {
      this.pageError.set(this.toUserError(err));
    } finally {
      this.isStarting.set(false);
    }
  }

  endCall(): void {
    this.twilio.endCall();
  }

  toggleMute(): void {
    this.twilio.toggleMute();
  }

  toggleSpeaker(): void {
    this.twilio.toggleSpeaker();
  }

  private buildCallerContext(): VoiceCallerContext | null {
    const userId = this.auth.getUserId();
    const tenantName = this.tenant.currentTenantName();
    if (!userId || !tenantName) {
      return null;
    }
    const role = this.auth.getOrganogramRoleForChat();
    const permissions = this.auth.getOrganogramPermissionsForChat();
    const userInfo = this.auth.getUserInfo();
    const fullName = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ').trim();
    const userName =
      (fullName || userInfo?.username || this.auth.getUserEmail() || '').trim() || null;

    return {
      userId,
      userName,
      tenantName,
      role,
      permissions: role ? permissions : [],
    };
  }

  private mergeState(twilio: CallUiState, stream: CallUiState): CallUiState {
    const rank: Record<CallUiState, number> = {
      error: 4,
      active: 3,
      connecting: 2,
      idle: 1,
    };
    return rank[twilio] >= rank[stream] ? twilio : stream;
  }

  displayStatus(): string {
    const err = this.pageError() ?? this.twilio.errorMessage();
    if (err) {
      return err;
    }

    const wsMsg = this.stream.statusMessage();
    if (wsMsg && this.uiState() === 'idle') {
      return wsMsg;
    }

    switch (this.uiState()) {
      case 'connecting':
        return VOICE_CALL_LABELS.connecting;
      case 'active':
        return VOICE_CALL_LABELS.connected;
      case 'error':
        return VOICE_CALL_LABELS.tokenFailed;
      default:
        return VOICE_CALL_LABELS.callEnded;
    }
  }

  private toUserError(err: unknown): string {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    if (msg.includes('permission') || msg.includes('notallowed')) {
      return VOICE_CALL_LABELS.micDenied;
    }
    if (msg.includes('not signed')) {
      return VOICE_CALL_LABELS.tokenFailed;
    }
    return VOICE_CALL_LABELS.tokenFailed;
  }
}
